import type { Screen } from '../../types';

const DEFAULT_WINDOW_WIDTH_FALLBACK = 1140;
const DEFAULT_WINDOW_HEIGHT_FALLBACK = 900;

const getViewportSize = () => {
  if (typeof window === 'undefined') {
    return {
      width: DEFAULT_WINDOW_WIDTH_FALLBACK,
      height: DEFAULT_WINDOW_HEIGHT_FALLBACK,
    };
  }

  const visualViewport = window.visualViewport;

  return {
    width: Math.round(
      visualViewport?.width ||
      window.innerWidth ||
      window.screen?.availWidth ||
      DEFAULT_WINDOW_WIDTH_FALLBACK
    ),
    height: Math.round(
      visualViewport?.height ||
      window.innerHeight ||
      window.screen?.availHeight ||
      DEFAULT_WINDOW_HEIGHT_FALLBACK
    ),
  };
};

export const getScreenWindowDimensions = (_screen: Screen) => {
  const viewport = getViewportSize();

  return {
    width: viewport.width,
    height: viewport.height,
  };
};

export const requestHostWindowResizeForScreen = (screen: Screen) => {
  const webview = window.chrome?.webview;
  if (!webview) {
    return;
  }

  const dimensions = getScreenWindowDimensions(screen);
  webview.postMessage(JSON.stringify({
    type: 'resize',
    width: dimensions.width,
    height: dimensions.height,
  }));
};

export type HostWindowMode = 'authentication' | 'workspace';

export const requestHostWindowMode = (mode: HostWindowMode) => {
  const webview = window.chrome?.webview;
  if (!webview) {
    return;
  }

  webview.postMessage(JSON.stringify({
    type: 'hostWindowMode',
    mode,
  }));
};

export type HostWindowLayoutPreset = 'CenterPanel';

export const requestHostWindowLayout = (preset: HostWindowLayoutPreset) => {
  const webview = window.chrome?.webview;
  if (!webview) {
    return;
  }

  webview.postMessage(JSON.stringify({
    type: 'hostWindowLayout',
    preset,
  }));
};

/**
 * Asks the WPF shell to hide the tool window. window.close() is a no-op in
 * the main WebView2 frame — only the host can close its own window. Returns
 * false outside the host so callers can fall back to window.close().
 */
export const requestHostWindowClose = (): boolean => {
  const webview = window.chrome?.webview;
  if (!webview) {
    return false;
  }

  webview.postMessage(JSON.stringify({ type: 'closeWindow' }));
  return true;
};
